<template>
  <section class="DASHBOARD-SECTION" :class="{ 'BORDER': bordered }">
    <transition name="FADE" mode="out-in">
      <div v-if="$slots.header" :class="['HEADER', headerClass]">
        <slot name="header" />
      </div>
    </transition>
    <div
        ref="wrapper"
        :style="{
          height: collapsed ? minVisibleContentHeight : actualContentHeight,
        }"
        class="COLLAPSE-WRAPPER"
        :class="{ 'COLLAPSED': collapsed, 'SHADOW': showShadow }">
      <div ref="content" :class="['CONTENT', contentClass]">
        <slot :calculateCollapsedHeight="debouncedCalculateHeight" />
      </div>
      <div
          ref="footer"
          class="FOOTER"
          :class="{ 'FOOTER-BORDER': $slots.footer }">
        <transition name="FADE" mode="out-in">
          <slot name="footer" />
        </transition>
      </div>
    </div>
  </section>
</template>

<script>
import _noop from 'lodash/noop';
import _debounce from 'lodash/debounce';

export default {
  name: 'dashboard-section',
  props: {
    headerClass: String,
    contentClass: String,
    bordered: Boolean,
    collapsed: Boolean,
    showShadow: {
      type: Boolean,
      default: true,
    },
    minVisibleContentHeight: {
      type: String,
      default: '0',
    },
  },
  data () {
    return {
      actualContentHeight: 'auto',
      debouncedCalculateHeight: _noop,
    };
  },
  watch: {
    collapsed () {
      if (!this.collapsed) {
        this.calculateHeight();
      }
    },
  },
  mounted () {
    this.debouncedCalculateHeight = _debounce(this.calculateHeight, 66);
  },
  methods: {
    calculateHeight () {
      const { height: contentHeight } = this.$refs.content.getBoundingClientRect();
      const { height: footerHeight } = this.$refs.footer.getBoundingClientRect();
      const {
        paddingTop: paddingTopPx,
        paddingBottom: paddingBottomPx,
      } = global.getComputedStyle(this.$refs.wrapper);
      const paddingTop = parseInt(paddingTopPx, 10);
      const paddingBottom = parseInt(paddingBottomPx, 10);
      let additionalHeight = 0;

      if (paddingTop && typeof paddingTop === 'number') {
        additionalHeight += paddingTop;
      }

      if (paddingBottom && typeof paddingBottom === 'number') {
        additionalHeight += paddingBottom;
      }

      this.actualContentHeight = `${contentHeight + footerHeight + additionalHeight}px`;
    },
  },
};
</script>

<style lang="scss" scoped>
  @import "~@homeservices/design-kit/tools";

  @import "~@/views/Dashboard/constants";

  .DASHBOARD-SECTION {
    @include padding-top(2, $md: 3);
    position: relative;
    display: flex;
    flex: 0 0 auto;
    flex-direction: column;
    background-color: white;
    border-top: 1px solid $color-gray30;

    &:first-of-type {
      border-top: none;
    }

    &.BORDER {
      &:last-of-type {
        .FOOTER {
          padding-bottom: 0;
        }
      }

      &:not(:last-of-type):after {
        content: '';
        @include absolute(auto, 0, 0, 0);
        width: 100%;
        height: $unit;
        background-color: $color-gray20;
        border-top: 1px solid $color-gray30;
      }

      .FOOTER-BORDER {
        border-top: 1px solid $color-gray30;
      }
    }
  }

  .HEADER {
    @include padding(0 2 2, $md: 0 5 2);
    display: flex;
  }

  .COLLAPSE-WRAPPER {
    position: relative;
    display: flex;
    flex-direction: column;
    will-change: height;
    transition: height $transition-duration;

    &.COLLAPSED {
      overflow-y: hidden;

      &.SHADOW:after {
        @include responsive(
          background-image,
          linear-gradient(
            to bottom,
            rgba(white, 0) 0%,
            rgba(white, 1) 75%
          ),
          linear-gradient(
            to bottom,
            rgba(white, 0) 0%,
            rgba(white, 1) 90%
          )
        );
        content: '';
        position: absolute;
        left: 0;
        bottom: 0;
        width: 100%;
        height: 100%;
      }
    }
  }

  .CONTENT {
    @include padding(0 2 2, $md: 0 5 3);
    display: flex;
    flex: 0 0 auto;
  }

  .FOOTER {
    @include padding(0 2 1, $md: 0 5 0);
    display: flex;
    flex: 0 0 auto;
  }
</style>
